import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import * as React from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  FormCheckbox,
  FormDateRangePicker,
  FormField,
  FormSelect,
} from '../../common/form';
import { PeriodSelect, PeriodSelectProps } from './PeriodSelect';
import translations from '../translations/PeriodSection.translations.json';
import { useSystemClassifierSelectOptions } from '../../configuration';
import { DataObjectType } from '../types';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';

const { addTranslations, useTranslation } = i18nNsFactory(
  'loading-period-fields',
);
addTranslations(translations);

interface PeriodSectionProps {
  index: number;
}

function PeriodSelectField({
  name,
  predefined,
  periodicity,
}: PeriodSelectProps) {
  const { t } = useTranslation();

  return (
    <PeriodSelectWrapper name={name} label={t(`field.${[periodicity]}`)}>
      <PeriodSelect
        name={name}
        predefined={predefined}
        periodicity={periodicity}
      />
    </PeriodSelectWrapper>
  );
}

export function PeriodSection({ index }: PeriodSectionProps) {
  const { t } = useTranslation();
  const [{ dataObjectType }] = DataObjectDescriptionVersionStore.useValue(
    'dataObjectDescriptionVersion',
  );
  const bankingDays = useWatch({
    name: `periods.${index}.bankingDays`,
  });
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);

  return (
    <Flex gap="middle" wrap="wrap">
      <PeriodSelectField
        name={`periods.${index}.minutes`}
        predefined="PREDEFINED_MINUTES"
        periodicity="PERIODICITY_MINUTES"
      />
      <PeriodSelectField
        name={`periods.${index}.hours`}
        predefined="PREDEFINED_HOURS"
        periodicity="PERIODICITY_HOURS"
      />
      <PeriodSelectField
        name={`periods.${index}.days`}
        predefined="PREDEFINED_DAYS"
        periodicity="PERIODICITY_DAYS"
      />
      <FormField
        name={`periods.${index}.bankingDays`}
        label={t('field.bankingDays')}
      >
        <FormCheckbox name={`periods.${index}.bankingDays`}>
          {t('field.bankingDays')}
        </FormCheckbox>
      </FormField>
      {!bankingDays && (
        <PeriodSelectField
          name={`periods.${index}.weekdays`}
          predefined="PREDEFINED_WEEKDAYS"
          periodicity="PERIODICITY_WEEKDAYS"
        />
      )}
      <PeriodSelectField
        name={`periods.${index}.months`}
        predefined="PREDEFINED_MONTHS"
        periodicity="PERIODICITY_MONTHS"
      />
      <FormField
        name={`periods.${index}.validPeriod`}
        label={t('field.validPeriod')}
      >
        <FormDateRangePicker
          name={`periods.${index}.validPeriod`}
          allowEmpty={[true, true]}
        />
      </FormField>
      {dataObjectType === DataObjectType.OUT && (
        <FormField
          name={`periods.${index}.sourceDataVersionId`}
          label={t('field.sourceDataVersionId')}
        >
          <FormSelect
            name={`periods.${index}.sourceDataVersionId`}
            options={sourceDataVersionOptions}
          />
        </FormField>
      )}
    </Flex>
  );
}

const PeriodSelectWrapper = styled(FormField)`
  min-width: 155px;
  max-width: 155px;
`;
